import { useDisplaySettings } from '@src/providers/DisplaySettingsProvider';
import { buildServerDrivenPositionSizeColumnFields } from '@src/utils/columns/serverDrivenPositionSizeColumn';
import {
  getAgGridColId,
  isTalosUser,
  useDefaultColumns,
  type Column,
  type ColumnDef,
  type Position,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useFeatureFlag, useUser } from '../../../../hooks';
import type { PositionColumn } from './../types';

export const usePositionsDerivativesColumns = (defaultColumns: PositionColumn[]): Column[] => {
  const user = useUser();
  const { homeCurrency } = useDisplaySettings();
  const {
    showPositionsBlotterWarningColumn,
    enableDerivativesPositionsBlotterAdvanced,
    enableServerDrivenNotionalAmount,
    enablePositionLegacyColumn,
  } = useFeatureFlag();

  const columnDefinitions = useMemo(() => {
    const primaryPosition = buildServerDrivenPositionSizeColumnFields<Position>({
      enableServerDrivenNotionalAmount,
      legacy: { field: 'Amount', currencyField: 'Asset' },
      server: {
        field: 'positionValue',
        currencyField: 'positionValueAsset',
        isValueAlreadyNotional: row => row.isPositionValueNotional,
      },
    });

    return new Map<string, Column>(
      (
        compact([
          showPositionsBlotterWarningColumn && {
            id: 'reconWarning',
            type: 'reconWarning',
            field: 'reconWarning',
            aggregate: true,
            sortable: true,
          },
          {
            id: 'underlying',
            type: 'positionUnderlyingAsset',
            title: 'Underlying',
          },
          {
            id: 'instrument',
            field: 'Asset',
            type: 'asset',
            sortable: true,
            title: 'Instrument',
            initialWidth: 200,
            params: {
              colorful: true,
            },
          },
          {
            id: 'MarketAccount',
            field: 'MarketAccount',
            type: 'marketAccount',
            sortable: true,
            initialSort: '-',
          },
          {
            id: 'Amount',
            field: primaryPosition.field,
            type: 'size',
            title: 'Position',
            sortable: true,
            aggregate: true,
            initialWidth: 150,
            params: {
              ...primaryPosition.params,
              highlightNegative: true,
              showCurrencyForNullPosition: false,
            },
            description: 'Position in underlying.',
          },
          enablePositionLegacyColumn
            ? {
                id: 'position-legacy',
                field: primaryPosition.legacy.field,
                type: 'size',
                title: 'Position (Legacy)',
                sortable: true,
                aggregate: true,
                initialWidth: 150,
                initialHide: true,
                params: {
                  ...primaryPosition.legacy.params,
                  highlightNegative: true,
                  showCurrencyForNullPosition: false,
                },
                description: 'Position in underlying.',
              }
            : false,
          {
            field: 'AvgCost',
            type: 'price',
            initialWidth: 150,
            params: {
              quoteCurrencyField: 'AvgCostCurrency',
            },
            description: 'Average cost for the instrument provided by the venue.',
          },
          {
            field: 'MarkPrice',
            type: 'price',
            initialWidth: 110,
            params: {
              quoteCurrencyField: 'MarkPriceCurrency',
            },
            description: 'Most recent mark price for the instrument provided by the venue.',
          },
          {
            field: 'UnderlyingPrice',
            type: 'price',
            initialWidth: 130,
            params: {
              quoteCurrencyField: 'UnderlyingQuoteCurrency',
            },
            description: "Most recent fair price for the instrument's underlying provided by the venue.",
          },
          {
            field: 'LiquidationPrice',
            type: 'price',
            initialWidth: 130,
            params: {
              assetField: 'Asset',
            },
            description: 'An estimated price at which the venue would initiate the liquidation process.',
          },
          {
            field: 'elpVsMp',
            type: 'percent',
            title: 'ELP vs MP (%)',
            initialWidth: 100,
            params: { increment: '0.01', round: true },
            description:
              'Percentage move in price needed from current mark levels to hit the estimated liquidation levels for a position.',
          },
          {
            field: 'Delta',
            type: 'delta',
            aggregate: true,
            initialWidth: 100,
            params: {
              currencyField: 'DeltaCurrency' satisfies keyof Position,
            },
            description:
              "Indicates how an instrument's price moves with its underlying. It's calculated by Talos for spot and derivatives, and provided by the venue for options.",
          },
          {
            field: 'Equivalent.Delta',
            type: 'size',
            title: `Delta (${homeCurrency})`,
            aggregate: true,
            initialWidth: 100,
            params: {
              currencyField: 'Equivalent.Currency',
              highlightNegative: true,
            },
            description:
              "Indicates how an instrument's price moves with its underlying, in home currency. It's calculated by Talos for spot and derivatives, and provided by the venue for options.",
          },
          {
            field: 'UnrealizedPnL',
            type: 'size',
            title: 'Unrealized PnL',
            params: {
              currencyField: 'PnLCurrency',
              highlightNegative: true,
            },
            description:
              'Unrealized P&L provided by the venue for the current position. The type of P&L (Life-To-Date or Session) is determined by the venue.',
          },
          {
            id: 'expiry',
            type: 'positionExpiry',
            title: 'Expiry',
            sortable: true,
            initialWidth: 80,
            initialHide: true,
            description: "Instrument's expiry date.",
          },
          {
            field: 'AssetType',
            title: 'Product Type',
            type: 'productTypeField',
            initialHide: true,
            initialWidth: 130,
          },
          {
            field: 'Amount',
            id: 'position-qty',
            type: 'size',
            title: 'Position Qty',
            description: 'Number of derivative contracts.',
            params: {
              showInTermsOfContracts: true,
              highlightNegative: true,
              currencyField: 'Asset',
              trimTrailingZeroes: true,
            },
            initialHide: true,
          },
          {
            type: 'size',
            field: 'Equivalent.Amount',
            title: `Position (${homeCurrency})`,
            params: { currencyField: 'Equivalent.Currency', highlightNegative: true },
            aggregate: true,
            initialHide: true,
            description: 'Position normalized in home currency terms, by using mark and underlying prices.',
          },
          {
            field: 'Gamma',
            type: 'gamma',
            initialWidth: 80,
            description:
              "Gamma measures the rate of change of option's delta with respect to changes in the underlying asset's price.",
          },
          {
            field: 'Vega',
            type: 'vega',
            initialWidth: 60,
            description:
              "Vega measures the rate of change (in USD) of an option's price in response to a +1 vol point move in implied volatility.",
          },
          {
            field: 'Theta',
            type: 'theta',
            initialWidth: 60,
            description:
              'Theta measures the change (in USD) in the price of an option for a one-day decrease in its time to expiration. ',
          },
          {
            field: 'Rho',
            type: 'rho',
            initialWidth: 60,
            description:
              "Rho measures the rate of change (in USD) of an option's price in response to a 1% shift in interest rate.",
          },
          {
            field: 'Equivalent.UnrealizedPnL',
            type: 'size',
            title: `Unrealized PnL (${homeCurrency})`,
            aggregate: true,
            params: {
              currencyField: 'Equivalent.Currency',
              highlightNegative: true,
            },
            initialHide: true,
            description: 'Unrealized P&L for current position converted to home currency.',
          },
          {
            field: 'LastUpdateTime',
            type: 'date',
            initialHide: true,
          },
          enableDerivativesPositionsBlotterAdvanced && {
            field: 'CumFees',
            type: 'size',
            title: 'Fees',
            params: {
              currencyField: 'FeeCurrency' satisfies keyof Position,
            },
            aggregate: true,
          },
          enableDerivativesPositionsBlotterAdvanced && {
            field: 'Equivalent.CumFees',
            type: 'size',
            title: `Fees (${homeCurrency})`,
            params: {
              currencyField: 'Equivalent.Currency',
            },
            aggregate: true,
            initialHide: true,
          },
          enableDerivativesPositionsBlotterAdvanced && {
            field: 'FundingPnL',
            type: 'size',
            title: 'Funding PnL',
            aggregate: true,
            params: {
              currencyField: 'FundingPnLCurrency',
              highlightNegative: true,
            },
            description: 'Cumulative funding payment paid or received in settlement currency.',
          },
          enableDerivativesPositionsBlotterAdvanced && {
            field: 'Equivalent.FundingPnL',
            type: 'size',
            title: `Funding PnL (${homeCurrency})`,
            params: {
              currencyField: 'Equivalent.Currency',
              highlightNegative: true,
            },
            aggregate: true,
            initialHide: true,
            description: 'Cumulative funding payment paid or received in home currency.',
          },
          enableDerivativesPositionsBlotterAdvanced && {
            field: 'RealizedPnL',
            type: 'size',
            title: 'Realized LTD PnL',
            params: {
              currencyField: 'PnLCurrency',
              highlightNegative: true,
            },
          },
          enableDerivativesPositionsBlotterAdvanced && {
            field: 'TotalPnL',
            type: 'size',
            title: 'Total LTD PnL',
            aggregate: true,
            params: {
              currencyField: 'PnLCurrency',
              highlightNegative: true,
            },
          },
          enableDerivativesPositionsBlotterAdvanced && {
            field: 'AvgCostCurrency',
            type: 'currency',
            initialHide: true,
          },
          enableDerivativesPositionsBlotterAdvanced && {
            field: 'PnLCurrency',
            type: 'currency',
            title: 'PnL Currency',
            initialHide: true,
          },
          isTalosUser(user) && {
            field: 'Status',
            type: 'text',
            initialHide: true,
          },
          isTalosUser(user) && {
            field: 'PositionSource',
            type: 'text',
            initialHide: true,
          },
        ] satisfies (ColumnDef<Position> | false)[]) satisfies ColumnDef<Position>[]
      ).map(c => [getAgGridColId(c), c])
    );
  }, [
    showPositionsBlotterWarningColumn,
    homeCurrency,
    user,
    enableDerivativesPositionsBlotterAdvanced,
    enableServerDrivenNotionalAmount,
    enablePositionLegacyColumn,
  ]);

  return useDefaultColumns(defaultColumns, columnDefinitions);
};
